import { BigNumber } from "@yjsgames/bignumber";
import type { CooldownEntry } from "@yjsgames/cooldown";
import type { GuideQuestSaveState } from "@yjsgames/guide-quest";
import type { ModuleInstance } from "./ModuleInstance.js";
import type { UpgradeInventoryState } from "./UpgradeInventory.js";
import { createInitialGridState, type GridPosition, type ModuleGridState } from "./ModuleGrid.js";
import { INITIAL_GOLD, INITIAL_DIAMOND } from "../data/balance.js";
import { DUNGEON_BALANCE } from "../data/rewardData.js";
import { DEFAULT_SPACESHIP_ID, defaultSpaceship, moduleGridStateForSpaceship } from "../data/spaceshipData.js";

/** 큰 숫자 표기 형식 설정값. auto = 언어 자동(동아시아 동양약어, 그 외 KMG). */
export type NumberFormat = "auto" | "kmg" | "eastAsian" | "alphabet";
export const DEFAULT_NUMBER_FORMAT: NumberFormat = "auto";

/**
 * 저장 데이터 스키마 버전.
 * v2 = 슬롯→그리드 전환. v3 = moduleId 카운트 → ModuleInstance 배열 + diamond/강화석/도감 (지시문 §17).
 * v4 = language 필드 추가 (현지화). v3→v4는 normalize에서 field-add 마이그레이션(진행 보존).
 * v5 = 모듈 footprintId 추가 (슬롯 다중 점유 모양). v3·v4→v5는 footprintId="single" field-add 마이그레이션(진행 보존).
 * v6 = gold/diamond를 BigNumber 직렬화 string으로 + numberFormat 표기 옵션. v3~v5→v6는 number→BigNumber 흡수.
 * v7 = 일일 리셋을 @yjsgames/daily-reset로 일반화 — dungeonLastResetUtcMs(단일) → dailyResets 맵(키별).
 *      v3~v6→v7은 dungeonLastResetUtcMs>0이면 dailyResets["dungeon-tickets"]로 이관.
 * v8 = 환생 강화 확장 — rebirthCount + rebirthUpgradeLevels(공격력/쿨타임/골드획득/포인트보너스, 환생 포인트 소모) 추가.
 *      구 permanentDamageBonus는 rebirthUpgradeLevels.attack으로 이관(공격력 스탯 통합). 폐기된 projectileSpeed 강화는 statUpgradeLevels 로드 시 자동 무시.
 * v9 = 모듈 그리드 회전 제거 — PlacedModule.rotation 폐기 + shapeId가 방향 명시 카탈로그(straight_h/corner_ru 등)로 전환.
 *      v8 이하 moduleGrid.placedModules는 구 shapeId(straight/corner)+rotation 체계라 normalize에서 [] 리셋(배치만 초기화; 인벤토리/골드/해금/강화는 보존, 모듈은 인벤토리에서 재배치).
 * v10 = 모듈 그리드 5×4 → 5×5 확장 + Start/Fire 중앙행(y=2) 이동. 차원·포트 변경으로 기존 배치/해금 경로가 무효.
 *       normalize 마이그레이션 조건은 v3~v8 그대로 유지(<=8 분기에서 진행 보존 + 배치만 리셋) → 이 범위는 새 게임이 아니다.
 *       v9 저장만 마이그레이션 분기(<=8) 밖이면서 현재 버전과 불일치 → version 비교에서 null 폐기(새 게임)로 처리.
 * v11 = 광고 제거 IAP — adFree(비소비성 구매 entitlement) 필드 추가. 그리드 차원 변화 없는 순수 field-add라
 *       v10→v11은 normalize에서 진행/배치/해금 전부 보존(배치 리셋 없음, adFree=false 백필). v9 이하는 여전히 폐기(분기 밖).
 *       이후 같은 v11에 tutorialCompleted/tutorialResume 추가(버전 무변경 — normalize {...defaults,...p}가 []/{} 백필, 진행 보존).
 * v12 = 모듈 융합(ModuleFusion) — ModuleInstance.fused(컴포넌트 2개) + moduleFusionUsedCount(일일 카운터) 추가.
 *       그리드 차원 변화 없는 순수 field-add라 v10/v11→v12는 진행/배치/해금 전부 보존(fused는 inst 스프레드로 자동 보존,
 *       moduleFusionUsedCount=0 백필). v9 이하는 여전히 폐기(분기 밖).
 * v13 = 모듈 footprint+shape 병합("조각 piece") — ModuleInstance.shapeId 추가(footprintId와 함께 획득 시 정의 조각 풀에서 pickPiece로 고정).
 *       구 전역 랜덤 footprint·배치 시 shapePool[0] 고정 폐기. v10~v12→v13은 finalize reassignPieces로 인스턴스 조각을
 *       정의 기본 조각(pieces[0])으로 재산정 + 배치 리셋(조각 변경으로 점유 무효), 해금/인벤토리/골드/강화 보존. v9 이하 폐기.
 * v14 = 미스터리(?) 블록 — mysteryDiamondGrantedToday/mysteryRelicGrantedToday(일일 보상 카운터) 추가.
 *       순수 field-add라 v10~v13→v14는 진행/배치/해금 전부 보존(normalize {...defaults,...p}가 0 백필 — moduleFusionUsedCount 선례 동형).
 *       자정 리셋 시각은 dailyResets["mystery-block"]가 SSoT. v9 이하는 여전히 폐기(분기 밖).
 * v15 = "스테이지" 개념 전면 폐기 → "챕터"로 rename. currentStage/bestStage 필드명을 currentChapter/bestChapter로 변경.
 *       세이브 호환 불필요(서비스 전) — 구 저장의 currentStage(extra)는 normalize에서 무시되고 currentChapter는 default 1로 백필(챕터 진행만 리셋, 골드/모듈/해금 등은 field-add 보존). v9 이하는 여전히 폐기.
 * v16 = 골드 상자 등급 천장 카운터 rarityPityCounter 추가 (1개·10개 구매 공용 천장).
 *       순수 field-add라 v10~v15→v16은 진행/배치/해금 전부 보존(normalize {...defaults,...p}가 0 백필). v9 이하는 여전히 폐기.
 * v17 = 모듈 레벨 소유권 이동 — 인스턴스(ModuleInstance.level)에서 **플레이어 자산(moduleLevels: moduleId→level)**으로.
 *       레벨은 같은 moduleId 인스턴스가 공유 + 환생 유지(등급은 인스턴스라 환생 소멸). 강화 비용도 강화석 전용(골드 제거).
 *       v10~v16→v17은 normalizeModuleLevels가 구 moduleInstances[].level을 moduleId별 max로 집계해 moduleLevels로 이관(진행 보존,
 *       인스턴스 level 필드는 폐기). moduleLevels 부재 시 빈 맵(=모든 모듈 Lv1). v9 이하는 여전히 폐기(분기 밖).
 * v18 = 상점 광고 다이아 — cooldowns(@yjsgames/cooldown entry 맵) + shopDailyAdDiamondClaimed(오늘의 광고 다이아 수령 여부) 추가.
 *       그리드 차원 변화 없는 순수 field-add라 v10~v17→v18은 진행/배치/해금 전부 보존(normalize {...defaults,...p}가
 *       cooldowns={} / shopDailyAdDiamondClaimed=false 백필 — adFree v11 선례 동형). v9 이하는 여전히 폐기(분기 밖).
 * v19 = 분석 마일스톤 — analyticsMilestones(string[], 계정 생애 1회 퍼널 이벤트 first_fire/first_purchase/rebirth_ready dedup) 추가.
 *       순수 field-add라 v10~v18→v19는 진행/배치/해금 전부 보존(normalize {...defaults,...p}가 [] 백필 — rarityPityCounter v16 선례 동형). v9 이하는 여전히 폐기(분기 밖).
 * v20 = 탭 해금 레드닷 — tabUnlockSeen(string[], 챕터 도달로 해금된 잠금 탭을 진입해 레드닷을 소비한 키 목록) 추가.
 *       순수 field-add라 v10~v19→v20은 진행/배치/해금 전부 보존(normalize {...defaults,...p}가 [] 백필 — analyticsMilestones v19 선례 동형). v9 이하는 여전히 폐기(분기 밖).
 * v21 = 모듈 숙련도 — moduleMastery(moduleId→{counters, selectedBranch?}) 추가. 모듈 사용 누적 카운터로 숙련도 노드 자동 해금.
 *       순수 field-add라 v10~v20→v21은 진행/배치/해금 전부 보존(normalize {...defaults,...p}가 {} 백필 + normalizeModuleMastery가 손상 카운터 0·selectedBranch A/B만 정규화). v9 이하는 여전히 폐기(분기 밖).
 * v22 = 시너지 도감 — discoveredSynergyIds(string[], 활성 경로 모듈 조합으로 발견한 시너지 id 목록, 최초 발견 보상 1회 dedup) 추가.
 *       순수 field-add라 v10~v21→v22는 진행/배치/해금/숙련도 전부 보존(normalize {...defaults,...p}가 [] 백필 — tabUnlockSeen v20 선례 동형). v9 이하는 여전히 폐기(분기 밖).
 * v23 = 가이드퀘스트 — guideQuest(퀘스트별 progress/completed/claimed) 추가. 데이터 주도 목표 레일의 진행 상태만 저장.
 *       순수 field-add라 v10~v22→v23은 진행/배치/해금 전부 보존(normalize {...defaults,...p}가 빈 상태 백필).
 * v24 = 시너지 강화 — synergyLevels(synergyId→level) 추가. 발견 보상 대신 항상 보이는 시너지 효과를 강화석으로 강화한다.
 *       순수 field-add라 v10~v23→v24는 진행/배치/해금 전부 보존(normalize {...defaults,...p}가 {} 백필).
 * v25 = 모듈 숙련도 XP 전환 — moduleMastery(moduleId→{xp, selectedBranch?})로 변경.
 *       v24 이하의 구 counters 기반 moduleMastery는 전부 초기화하고, 다른 진행 데이터는 보존한다.
 * v26 = 모듈 그리드 초기 해금 축소 — 중앙행 5칸만 기본 해금. v25 이하의 구 무료 하단 3칸은
 *       저장 복원에서 배치가 없을 때 잠금으로 되돌려 첫 화면 5칸 시작과 해금 비용 기준을 맞춘다.
 * v27 = 우주선별 배선 그리드 — selectedSpaceshipId 추가 + 기본 우주선 2x3 그리드로 전환.
 *       구 저장은 선택 id를 기본 우주선으로 백필하고, 그리드 배치/해금은 새 우주선 레이아웃으로 리셋한다.
 * v28 = 우주선/우주선 모듈 슬롯 소유권 분리 — ownedSpaceshipIds + spaceshipModuleUnlocks 추가.
 *       우주선 소유와 각 우주선 내부 슬롯 해금은 Firestore payload에 독립 영속된다.
 * v29 = 다이어로그 읽음 상태 — dialogueSeenIds(string[]) 추가. TSV 기반 선택형 교신의 once 표시를 저장한다.
 * v30 = pioneer 우주선 gridMap 변경(3x2 LLL/LLL → 2x3 ../LL/LL) — 상단 2칸 다이아 잠금 도입.
 *       v29 이하 저장에서 pioneer의 {0,0},{1,0} 해금 셀을 강제 제거해 새 잠금 구조와 일치시킨다.
 * v2 이하 저장은 normalize에서 폐기(새 게임) — v1→v2 선례.
 *
 * ⚠ 밸런스 base 스케일 변경(예: 2026-06-12 baseDamage·attack base ×10)은 버전 증가/마이그레이션 불필요 —
 *   저장되는 건 업그레이드 "레벨"뿐이고 효과는 expression({base}*{level})으로 런타임 재계산되므로
 *   TSV base 변경만으로 구 세이브도 자동 동률 스케일된다(v8 permanentDamageBonus→attack 이관 레벨 포함).
 */
export const MODULE_BRICK_SAVE_VERSION = 30;

/**
 * 한 모듈의 숙련도 상태 — 누적 XP + 레벨 3 분기 선택(A/B, 미선택 시 분기 노드 미적용).
 */
export interface ModuleMasteryState {
  xp: number;
  selectedBranch?: "A" | "B";
}

/** moduleId → 회차 숙련도 상태 (앱 재실행 복원용 저장, 환생 시 초기화, 인스턴스별 저장 금지). */
export type ModuleMasterySaveState = Record<string, ModuleMasteryState>;

/** 일일 리셋 키 → 마지막 리셋 시각(로컬 자정 epoch ms) 맵 SSoT (@yjsgames/daily-reset ResetStore가 위임 저장). */
export const DAILY_RESET_KEY_DUNGEON_TICKETS = "dungeon-tickets";
/** 일일상점 자정 리셋 키 (dailyResets 맵 + DailyResetService 등록 → 슬롯 재추첨/카운터 리셋). */
export const DAILY_RESET_KEY_DAILY_SHOP = "daily-shop";
/** 모듈 융합 일일 리셋 키 (dailyResets 맵 + DailyResetService 등록 → 사용 횟수 0 복귀 → 다시 첫 회 광고). */
export const DAILY_RESET_KEY_MODULE_FUSION = "module-fusion";
/** 미스터리(?) 블록 일일 보상(다이아·유물) 리셋 키 (dailyResets 맵 + DailyResetService 등록 → 두 카운터 0 복귀). */
export const DAILY_RESET_KEY_MYSTERY = "mystery-block";
/** 오늘의 광고 다이아 일일 리셋 키 (dailyResets 맵 + DailyResetService 등록 → shopDailyAdDiamondClaimed=false 복귀). */
export const DAILY_RESET_KEY_SHOP_AD_DIAMOND = "shop-ad-diamond";

/**
 * 모듈 벽돌깨기 전용 저장 타입 (지시문 §17). 보유 모듈은 ModuleInstance 배열,
 * 강화 재료는 upgradeInventory(강화석), 도감 발견은 discoveredDefinitionIds로 추적.
 */
export interface ModuleBrickSaveData {
  version: number;
  /** 보유 골드 — BigNumber invariant 직렬화 string (무한 성장). */
  gold: string;
  /** 보유 다이아 — BigNumber invariant 직렬화 string. */
  diamond: string;
  currentChapter: number;
  bestChapter: number;
  moduleInstances: ModuleInstance[];
  /**
   * 모듈 레벨 (v17) — definitionId(moduleId) → 플레이어 영구 레벨(>=2만 저장, 미존재=Lv1).
   * 인스턴스가 아니라 플레이어 자산이라 같은 moduleId 인스턴스가 공유 + 환생 유지. PlayerModuleLevelController.toState() 위임.
   * v16 이하 마이그레이션 시 구 moduleInstances[].level을 moduleId별 max로 집계해 채운다.
   */
  moduleLevels: Record<string, number>;
  /**
   * 모듈 숙련도 (v25) — moduleId → {누적 XP, 분기 선택}. 회차 자산이라 환생 시 초기화하며, 인스턴스별 저장은 금지.
   * ModuleMasteryController.toState() 위임. normalize {...defaults,...p}가 {} 백필 + normalizeModuleMastery로 정규화.
   */
  moduleMastery: ModuleMasterySaveState;
  upgradeInventory: UpgradeInventoryState;
  discoveredDefinitionIds: string[];
  /**
   * 발견한 시너지 id 목록 (v22) — 활성 경로 모듈 조합으로 발동한 시너지를 최초 1회 기록(보상 dedup).
   * ModuleSynergyController.toState() 위임 직렬화. normalize {...defaults,...p}가 [] 백필(진행 보존, tabUnlockSeen v20 선례 동형).
   */
  discoveredSynergyIds: string[];
  /**
   * 시너지 강화 레벨 (v24) — synergyId → 영구 레벨(>=2만 저장, 미존재=Lv1).
   * SynergyLevelController.toState() 위임. 발견 여부와 무관하게 도감에 항상 보이는 시너지 효과 강화 상태다.
   */
  synergyLevels: Record<string, number>;
  /** 선택한 우주선 id. 우주선 변경 시 moduleGrid는 해당 우주선 레이아웃으로 리셋되고 배치 모듈은 전부 해제된다. */
  selectedSpaceshipId: string;
  /** 소유한 우주선 id 목록. 기본 우주선은 항상 포함된다. Firestore 동기화 대상. */
  ownedSpaceshipIds: string[];
  /** 우주선별 소유/해금한 내부 모듈 슬롯 좌표. shipId → unlocked grid cells. Firestore 동기화 대상. */
  spaceshipModuleUnlocks: Record<string, GridPosition[]>;
  moduleGrid: ModuleGridState;
  rebirthPoint: number;
  /** 누적 환생 횟수 (v8) — 환생 가능 조건 누진 `({rebirthCount}+1)*3`의 입력. */
  rebirthCount: number;
  /** 골드 소모 영구 스탯 강화 레벨 (statId → level). 미존재 statId는 레벨 0. 폐기된 projectileSpeed는 로드 시 자동 무시. */
  statUpgradeLevels: Record<string, number>;
  /**
   * 환생 포인트 소모 영구 강화 레벨 (v8 — attack/fireRate/goldGain/pointBonus). statId → level.
   * 구 permanentDamageBonus는 normalize에서 attack 레벨로 이관(공격력 스탯 통합 흡수).
   */
  rebirthUpgradeLevels: Record<string, number>;
  /** 유물 누적 보유 개수 (relicId → count). 레벨 = count, 효과 = effectExpr×level. normalize에서 구 ownedRelicIds 배열 → {id:1} 마이그레이션. */
  relicCounts: Record<string, number>;
  /** 골드 상자 누적 개봉 수 (가격 진행 + pity 복원용). */
  boxOpenCount: number;
  pityCounter: number;
  /**
   * 등급 천장 누적 카운터 (v16 field-add) — 1개·10개 구매 공용. pityInterval회마다 보장 pull(등급 floor 상향).
   * @yjsgames/pity PityCounter 상태. normalize {...defaults,...p}가 0 백필(진행 보존, moduleFusionUsedCount 선례 동형).
   */
  rarityPityCounter: number;
  selectedTab: string;
  effectLevel: number;
  /** 도감을 마지막으로 연 시점의 bestChapter — 그 이후 새로 해금된 조각이 있으면 도감 탭에 레드닷(codexRedDot). 도감 진입 시 bestChapter로 갱신. */
  codexSeenChapter: number;
  /** 챕터 도달로 해금된 잠금 탭(codex/moduleMerge/rebirth)을 진입해 해금 레드닷(tabUnlockDot)을 소비한 키 목록. 해금 후 첫 진입 시 추가. */
  tabUnlockSeen: string[];
  /** 읽음 처리한 다이어로그 id 목록. dialogues.tsv의 once=true 교신 버튼 숨김/깜빡임 판정에 사용한다. */
  dialogueSeenIds: string[];
  // 설정(soundEnabled/language/numberFormat)은 기기별 설정이라 진행 데이터에서 분리됨 → BrickSettings(로컬 전용 BrickSettingsStore).
  // version은 유지(v10) — normalize {...defaults,...p}가 구 저장의 잔존 설정 필드를 무시(extra 무해). 기존 값은 GameScene 첫 부팅에서 BrickSettings로 1회 이전.
  /** 던전 현재 도전 인덱스 (0-based, 무한 진행). normalize {...defaults,...p}로 field-add 자동 보정. */
  dungeonCurrentIndex: number;
  /** 클리어한 최고 던전 인덱스 (-1=없음). 소탕 가능 조건. */
  dungeonClearedMaxIndex: number;
  /** 남은 일일 도전권 (자정 리셋). */
  dungeonTicketsRemaining: number;
  /**
   * 일일 리셋 키 → 마지막 리셋 시각(로컬 자정 epoch ms) 맵 (v7 추가).
   * @yjsgames/daily-reset의 ResetStore가 SaveDataResetStore 어댑터로 위임 저장. 던전 도전권 = "dungeon-tickets" 키.
   */
  dailyResets: Record<string, number>;
  /**
   * 일일상점 현재 6슬롯 (poolId/할인%/구매 sold-out). 자정 리셋 시각은 dailyResets["daily-shop"]가 SSoT.
   * normalize {...defaults,...p} 스프레더로 field-add 자동 보정 (빈 배열이면 DailyShopController가 재추첨).
   */
  dailyShopSlots: { poolId: string; discountPercent: number; purchased: boolean }[];
  /** 일일상점 오늘 무료 리롤 사용 여부 (자정 리셋 시 false). */
  dailyShopFreeRerollUsed: boolean;
  /** 일일상점 오늘 다이아 리롤 누진 step (자정 리셋 시 0). */
  dailyShopRerollStep: number;
  /**
   * 모듈 융합 오늘 사용 횟수 (v12, 자정 리셋 시 0). 0이면 첫 회 광고, 1+면 다이아 고정(global.tsv fusionDiamondCost).
   * 자정 리셋 시각은 dailyResets["module-fusion"]가 SSoT. ModuleFusionController.toState()가 위임 직렬화.
   */
  moduleFusionUsedCount: number;
  /**
   * 미스터리(?) 블록 오늘 다이아 지급 횟수 (v14, 자정 리셋 시 0). 한도는 global.tsv mysteryDiamondDailyLimit.
   * 자정 리셋 시각은 dailyResets["mystery-block"]가 SSoT. MysteryBlockController.toState()가 위임 직렬화.
   */
  mysteryDiamondGrantedToday: number;
  /** 미스터리(?) 블록 오늘 유물 지급 횟수 (v14, 자정 리셋 시 0). 한도는 global.tsv mysteryRelicDailyLimit. */
  mysteryRelicGrantedToday: number;
  /**
   * 광고 제거 IAP 보유 여부 (v11, 비소비성 구매 entitlement). true면 일일상점 ad 슬롯을 광고 없이 즉시 수령.
   * 클라우드 동기화 대상(계정 귀속) — 네이티브는 restorePurchases가 SSoT(부팅 시 백필), 본 필드는 캐시.
   */
  adFree: boolean;
  /**
   * 쿨타임 entry 맵 (v18) — 키(상품 id 등) → {startedAtMs, durationMs}. @yjsgames/cooldown CooldownStore가
   * SaveDataCooldownStore 어댑터로 위임 저장. 상점 2시간 쿨타임 광고 다이아 = "ad_diamond_cd" 키.
   */
  cooldowns: Record<string, CooldownEntry>;
  /** 오늘의 광고 다이아 수령 여부 (v18, 자정 리셋 시 false). dailyResets["shop-ad-diamond"]가 리셋 시각 SSoT. */
  shopDailyAdDiamondClaimed: boolean;
  /** 가이드퀘스트 진행 상태 (v23 field-add) — 정의는 guideQuests.tsv, 저장은 진행/완료/수령 dedup만 보유. */
  guideQuest: GuideQuestSaveState;
  /**
   * 완료한 튜토리얼 시나리오 id 목록 (v11 field-add — 순수 추가, 진행 보존).
   * SaveDataTutorialStore가 위임 접근(in-place push). 완료 시나리오는 startScenario에서 자동 스킵.
   */
  tutorialCompleted: string[];
  /** 튜토리얼 시나리오 id → 재개 스텝 인덱스 (중단 지점 복원). 완료 시 0 리셋. */
  tutorialResume: Record<string, number>;
  /**
   * 발화 완료한 분석 마일스톤 키 목록 (v19) — 계정 생애 1회 퍼널 이벤트(first_fire/first_purchase/rebirth_ready)
   * dedup. AnalyticsMilestoneController.toState() 위임 직렬화. normalize {...defaults,...p}가 [] 백필(진행 보존).
   */
  analyticsMilestones: string[];
  lastSavedAtUtcMs: number;
}

export function createDefaultModuleBrickSaveData(): ModuleBrickSaveData {
  return {
    version: MODULE_BRICK_SAVE_VERSION,
    gold: BigNumber.fromNumber(INITIAL_GOLD).toString(),
    diamond: BigNumber.fromNumber(INITIAL_DIAMOND).toString(),
    currentChapter: 1,
    bestChapter: 1,
    moduleInstances: [],
    moduleLevels: {},
    moduleMastery: {},
    upgradeInventory: { universalUpgradeStone: 0 },
    discoveredDefinitionIds: [],
    discoveredSynergyIds: [],
    synergyLevels: {},
    selectedSpaceshipId: DEFAULT_SPACESHIP_ID,
    ownedSpaceshipIds: [DEFAULT_SPACESHIP_ID],
    spaceshipModuleUnlocks: {
      [DEFAULT_SPACESHIP_ID]: moduleGridStateForSpaceship(defaultSpaceship()).unlockedCells,
    },
    moduleGrid: moduleGridStateForSpaceship(defaultSpaceship()) ?? createInitialGridState(),
    rebirthPoint: 0,
    rebirthCount: 0,
    statUpgradeLevels: {},
    rebirthUpgradeLevels: {},
    relicCounts: {},
    boxOpenCount: 0,
    pityCounter: 0,
    rarityPityCounter: 0,
    selectedTab: "battle",
    effectLevel: 1,
    codexSeenChapter: 1,
    tabUnlockSeen: [],
    dialogueSeenIds: [],
    dungeonCurrentIndex: 0,
    dungeonClearedMaxIndex: -1,
    dungeonTicketsRemaining: DUNGEON_BALANCE.dailyChallengeLimit,
    dailyResets: {},
    dailyShopSlots: [],
    dailyShopFreeRerollUsed: false,
    dailyShopRerollStep: 0,
    moduleFusionUsedCount: 0,
    mysteryDiamondGrantedToday: 0,
    mysteryRelicGrantedToday: 0,
    adFree: false,
    cooldowns: {},
    shopDailyAdDiamondClaimed: false,
    guideQuest: { progress: {} },
    tutorialCompleted: [],
    tutorialResume: {},
    analyticsMilestones: [],
    lastSavedAtUtcMs: 0,
  };
}
